/** Pure HTML-string renderers for the History page.
 *
 * Layer: `view` per docs/design/spa-account-extraction-260524.md §3.6.
 *
 * Purity contract: every exported function accepts typed data and returns a
 * string. No DOM access, no fetch, no window.X mutations — consumers do
 * `el.innerHTML = render*(...)`.
 *
 * Extracted from web/static/history.html (Phase AC.8). history.html is
 * unchanged until AC.9 wires the module tag.
 */
// ─── Formatter helpers (pure, exported for tests) ─────────────────────────
export function fmtDateTime(iso) {
    if (!iso)
        return '—';
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    }
    catch (_e) {
        return iso;
    }
}
export function fmtSize(bytes) {
    if (!bytes)
        return '—';
    if (bytes < 1024)
        return bytes + ' B';
    if (bytes < 1048576)
        return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
}
export function fmtDuration(secs) {
    if (!secs)
        return '—';
    if (secs < 60)
        return secs.toFixed(0) + 's';
    return (secs / 60).toFixed(1) + 'm';
}
// ─── Status badge ─────────────────────────────────────────────────────────
export function renderStatusBadge(esc, status) {
    return `<span class="status-badge ${esc(status)}">${esc(status)}</span>`;
}
// ─── Job row ──────────────────────────────────────────────────────────────
export function renderJobRowsHtml(esc, jobs) {
    if (!jobs.length)
        return '';
    return jobs.map(job => {
        const j = job;
        const canDownload = j.status === 'completed' || j.status === 'checked';
        let downloadCell = '—';
        if (canDownload) {
            downloadCell = `<a href="/api/jobs/${encodeURIComponent(j.id)}/download-original" class="account-btn" style="padding:3px 8px;font-size:12px">Original</a>`;
            downloadCell += ` <a href="/api/jobs/${encodeURIComponent(j.id)}/download" class="account-btn" style="padding:3px 8px;font-size:12px;background:var(--accent);color:#fff">Remediated</a>`;
        }
        const deleteBtn = `<button class="account-btn job-delete-btn" data-action="delete-job" data-job-id="${esc(j.id)}">&times;</button>`;
        return `<tr data-job-id="${esc(j.id)}">
                <td class="job-filename-cell" title="${esc(j.filename)}">${esc(j.filename)}</td>
                <td>${renderStatusBadge(esc, j.status || '')}</td>
                <td>${fmtDateTime(j.created_at)}</td>
                <td>${fmtSize(j.file_size_bytes)}</td>
                <td>${fmtDuration(j.duration_seconds)}</td>
                <td>${downloadCell}</td>
                <td>${deleteBtn}</td>
            </tr>`;
    }).join('');
}
// ─── Job table ────────────────────────────────────────────────────────────
export function renderJobTableHtml(esc, jobs) {
    if (!jobs.length) {
        return '<p style="color:var(--text-muted);font-size:14px;font-style:italic">No jobs yet.</p>';
    }
    return `<div class="job-history-scroll" tabindex="0" role="region" aria-label="Job history (scrollable)">
            <table class="job-history-table">
                <thead>
                    <tr>
                        <th>File</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th>Size</th>
                        <th>Time</th>
                        <th>Download</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody id="job-tbody">
                    ${renderJobRowsHtml(esc, jobs)}
                </tbody>
            </table>
        </div>`;
}
